import axios from 'axios';
import { loadConfig } from '../env/config_util';

const config = loadConfig();

const api = axios.create({
  baseURL: config.config_module.base_url,
  withCredentials: true,
});

// Attach auth token from localStorage (set during login)
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('access_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ── Deal Room ──────────────────────────────────────────
export const getDealRoom = (startupId) => api.get(`/api/dealroom/${startupId}`);
export const requestDealRoomAccess = (startupId) => api.post(`/api/dealroom/${startupId}/access`);
export const checkDealRoomAccess = (startupId) => api.get(`/api/dealroom/${startupId}/access`);

// ── Documents ──────────────────────────────────────────
export const listDocumentsByStartup = (startupId) => api.get(`/api/documents/startup/${startupId}`);
export const uploadDocument = (formData) => api.post('/api/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const downloadDocument = (id) => api.get(`/api/documents/${id}`, { responseType: 'blob' });
export const deleteDocument = (id) => api.delete(`/api/documents/${id}`);

// ── Q&A ────────────────────────────────────────────────
export const listQAThreads = (startupId) => api.get(`/api/qa/startup/${startupId}`);
export const createQAThread = (startupId, data) => api.post(`/api/qa/startup/${startupId}`, data);
export const replyToThread = (threadId, data) => api.post(`/api/qa/thread/${threadId}/reply`, data);
export const editThread = (threadId, data) => api.put(`/api/qa/thread/${threadId}`, data);
export const deleteThread = (threadId) => api.delete(`/api/qa/thread/${threadId}`);

// ── Portfolio ──────────────────────────────────────────
export const getPortfolioSummary = () => api.get('/api/portfolio');
export const getPortfolioInvestments = () => api.get('/api/portfolio/investments');
export const getPortfolioPerformance = () => api.get('/api/portfolio/performance');
export const getPortfolioBySector = () => api.get('/api/portfolio/by-sector');
export const getPortfolioDistributions = () => api.get('/api/portfolio/distributions');

// ── Admin ──────────────────────────────────────────────
export const getAdminStats = () => api.get('/api/admin/stats');
export const listAdminUsers = (params) => api.get('/api/admin/users', { params });
export const getAdminUserDetail = (id) => api.get(`/api/admin/users/${id}`);
export const updateAdminUser = (id, data) => api.put(`/api/admin/users/${id}`, data);
export const listAdminDeals = (params) => api.get('/api/admin/deals', { params });
export const createAdminDeal = (data) => api.post('/api/admin/deals', data);
export const updateAdminDeal = (id, data) => api.put(`/api/admin/deals/${id}`, data);
export const listAdminInvestments = (params) => api.get('/api/admin/investments', { params });
export const getAuditLog = (params) => api.get('/api/admin/audit-log', { params });
export const getComplianceOverview = () => api.get('/api/admin/compliance/overview');
export const getOnboardingPipeline = () => api.get('/api/admin/onboarding/pipeline');
export const advanceOnboarding = (id) => api.put(`/api/admin/onboarding/${id}/advance`);
export const rejectOnboarding = (id) => api.put(`/api/admin/onboarding/${id}/reject`);

// ── KYC & Accreditation ────────────────────────────────
export const submitKyc = (data) => api.post('/api/kyc/submit', data);
export const getKycStatus = () => api.get('/api/kyc/status');
export const submitAccreditation = (data) => api.post('/api/accreditation/submit', data);
export const getAccreditationStatus = () => api.get('/api/accreditation/status');

// ── Investments & Flow ─────────────────────────────────
export const listInvestments = () => api.get('/api/investments');
export const createInvestment = (data) => api.post('/api/investments', data);
export const expressInterest = (data) => api.post('/api/investments/express-interest', data);
export const createInvestmentFlow = (data) => api.post('/api/investments/create', data);
export const signInvestment = (id, data) => api.post(`/api/investments/${id}/sign`, data);
export const fundInvestment = (id, data) => api.post(`/api/investments/${id}/fund`, data);

// ── Payments ───────────────────────────────────────────
export const createPaymentIntent = (data) => api.post('/api/payments/create-intent', data);
export const getWireInstructions = (investmentId) => api.get(`/api/payments/wire-instructions/${investmentId}`);
export const getPaymentStatus = (investmentId) => api.get(`/api/payments/status/${investmentId}`);

// ── Notifications ──────────────────────────────────────
export const listNotifications = (params) => api.get('/api/notifications', { params });
export const getUnreadCount = () => api.get('/api/notifications/unread-count');
export const markAllNotificationsRead = () => api.put('/api/notifications/read-all');
export const markNotificationRead = (id) => api.put(`/api/notifications/${id}/read`);

// ── Reports ────────────────────────────────────────────
export const getQuarterlyReport = (year, quarter) => api.get(`/api/reports/quarterly/${year}/${quarter}`);
export const getTaxReport = (year) => api.get(`/api/reports/tax/${year}`);
export const exportCSV = () => api.get('/api/reports/export/csv', { responseType: 'blob' });
export const exportPDF = () => api.get('/api/reports/export/pdf', { responseType: 'blob' });

// ── Updates ────────────────────────────────────────────
export const listStartupUpdates = (startupId) => api.get(`/api/updates/startup/${startupId}`);
export const createStartupUpdate = (startupId, data) => api.post(`/api/updates/startup/${startupId}`, data);
export const editStartupUpdate = (id, data) => api.put(`/api/updates/${id}`, data);
export const deleteStartupUpdate = (id) => api.delete(`/api/updates/${id}`);

export default api;
